import React, { useState, useEffect, useMemo, useRef } from 'react';
import { track } from '../services/analyticsTracker';
import { useNavigate } from 'react-router-dom';
import { usersAPI, condosAPI, unitsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatUnitLabel } from '../utils/unitLabel';

// BUG #157 (2026-09-24): single source of truth for the roles offered in the
// Editar Usuario selector, shared by openEditUser prefill and the modal.
const EDITABLE_ROLES: readonly string[] = ['admin', 'sub_admin', 'treasurer', 'propietario', 'resident', 'familiar', 'empleado', 'security', 'provider', 'maintenance_staff'];

export default function UserManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [condos, setCondos] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', role: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({ key: 'name', direction: 'asc' });
  // Percentages keep the table inside its container while still allowing each
  // column to be resized. The final column is deliberately compact.
  const columnKeys = ['name', 'email', 'role', 'condo', 'unit', 'actions'] as const;
  type ColumnKey = typeof columnKeys[number];
  const defaultColumnWidths: Record<ColumnKey, number> = { name: 18, email: 25, role: 17, condo: 22, unit: 10, actions: 8 };
  const [columnWidths, setColumnWidths] = useState<Record<ColumnKey, number>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('umg-col-widths') || 'null');
      return saved && columnKeys.every((key) => typeof saved[key] === 'number' && saved[key] >= 1) ? { ...defaultColumnWidths, ...saved } : defaultColumnWidths;
    } catch { return defaultColumnWidths; }
  });
  const resizeState = useRef<{ key: ColumnKey; nextKey: ColumnKey; startX: number; startWidths: Record<ColumnKey, number>; tableWidth: number } | null>(null);

  useEffect(() => {
    localStorage.setItem('umg-col-widths', JSON.stringify(columnWidths));
  }, [columnWidths]);

  const beginColumnResize = (event: React.PointerEvent, key: ColumnKey) => {
    const index = columnKeys.indexOf(key);
    if (index === columnKeys.length - 1) return;
    const table = event.currentTarget.closest('table');
    if (!table) return;
    resizeState.current = { key, nextKey: columnKeys[index + 1], startX: event.clientX, startWidths: { ...columnWidths }, tableWidth: table.getBoundingClientRect().width };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    const move = (event: PointerEvent) => {
      const active = resizeState.current;
      if (!active || active.tableWidth <= 0) return;
      const delta = (event.clientX - active.startX) / active.tableWidth * 100;
      const min = 60 / active.tableWidth * 100;
      const current = active.startWidths[active.key];
      const next = active.startWidths[active.nextKey];
      const adjusted = Math.max(min, Math.min(current + next - min, current + delta));
      const nextAdjusted = current + next - adjusted;
      setColumnWidths((widths) => ({ ...widths, [active.key]: adjusted, [active.nextKey]: nextAdjusted }));
    };
    const end = () => { resizeState.current = null; document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', end); };
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'resident',
    condoId: '',
    unit: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const isSuperAdmin = user?.role === 'super_admin';
      const condoIds = Array.from(new Set([user?.condoId, ...((user as any)?.memberships || []).map((m: any) => m.condoId || m.condo_id)].filter(Boolean))) as string[];
      const [userResponses, condoResponses] = await Promise.all([
        isSuperAdmin ? Promise.all([usersAPI.getAll()]) : Promise.all(condoIds.map((id) => usersAPI.getAll(id))),
        isSuperAdmin ? Promise.all([condosAPI.getAll()]) : Promise.all(condoIds.map((id) => condosAPI.getAll(id))),
      ]);
      const usersData = userResponses.flatMap((r) => r.data?.data?.users ?? r.data?.users ?? []);
      const condosData = condoResponses.flatMap((r) => r.data?.data?.condos ?? r.data?.data?.items ?? r.data?.condos ?? r.data?.items ?? []);
      setUsers(usersData);
      setCondos(condosData.filter((c, i, all) => all.findIndex((x) => x.id === c.id) === i));
    } catch (err) {
      setError('Error al cargar datos');
      setUsers([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    setFormData((current) => ({ ...current, unit: '' }));
    setUnits([]);
    if (!formData.condoId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await unitsAPI.getAvailable(formData.condoId);
        if (!cancelled) setUnits(response.data?.data?.units ?? response.data?.units ?? []);
      } catch {
        try { const response = await unitsAPI.getAll(formData.condoId); if (!cancelled) setUnits(response.data?.data?.units ?? response.data?.units ?? []); } catch { if (!cancelled) setUnits([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [formData.condoId]);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    // B2-1 (2026-10-01): never silently no-op - surface a validation error
    // instead of firing a malformed POST when condoId is empty.
    if (!formData.condoId) {
      setError('Selecciona un condominio para el nuevo usuario');
      return;
    }
    try {
      await condosAPI.addMember(formData.condoId, { ...formData, unitId: formData.unit || undefined });
      track('admin_user_invited');
      setShowAddModal(false);
      loadData();
      setFormData({ name: '', email: '', password: '', role: 'resident', condoId: '', unit: '' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al crear usuario');
    }
  };

  // BUG #157 (2026-09-24): the role selector must prefill with the SAME role
  // the row renders — displayRole(membership, account) — not the raw
  // candidate.role field, which can be stale/empty while the real membership
  // role lives elsewhere (e.g. user_role). If the displayed role is not one of
  // the selectable stored roles (display-only transforms like visitante /
  // unreliable data), fall back to the placeholder so an untouched save sends
  // NO role field and can never silently flip the membership role.
  const openEditUser = (candidate: any) => {
    const displayedRole = effectiveMembershipRole(candidate);
    const prefill = EDITABLE_ROLES.includes(displayedRole) ? displayedRole : '';
    setEditingUser(candidate);
    setEditForm({ name: displayName(candidate), email: candidate.email || '', phone: candidate.phone || '', role: prefill });
    setError('');
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    setSavingEdit(true);
    try {
      // BUG #150 (2026-09-24): role selector — include the membership role +
      // condo context ONLY when it actually changed, so PUT /api/users/:id
      // re-titles the existing membership (e.g. empleado→residente) instead of
      // creating anything new. Profile fields keep their existing contract.
      // BUG #157: the guard compares against the DISPLAYED membership role
      // (same source of truth as openEditUser prefill), not the raw
      // editingUser.role field. An empty editForm.role is the
      // "— mantener rol actual —" placeholder and never triggers a change.
      const roleChanged = Boolean(editForm.role) && editForm.role !== effectiveMembershipRole(editingUser);
      // NOTE: role is NOT spread from editForm anymore — previously {...editForm}
      // leaked role on EVERY save, silently rewriting the membership role even
      // when the selector was untouched. role is only included when it changed.
      const payload: { name: string; email: string; phone: string | null; role?: string; condoId?: string } = { name: editForm.name, email: editForm.email, phone: editForm.phone || null };
      if (roleChanged) {
        payload.role = editForm.role;
        payload.condoId = editingUser.condo_id || editingUser.condoId;
      }
      await usersAPI.update(editingUser.id, payload);
      setEditingUser(null);
      setError('');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al actualizar usuario');
    } finally { setSavingEdit(false); }
  };

  const handleDeleteUser = async (id: string) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
    try {
      await usersAPI.delete(id);
      loadData();
    } catch (err) {
      setError('Error al eliminar usuario');
    }
  };

  // Beta-user toggle — super_admin only (Jaime directive). The users-list payload
  // includes is_beta; if it is undefined for an account, the toggle is hidden
  // rather than assuming a default value.
  const isSuperAdminViewer = user?.role === 'super_admin';
  const [betaUpdating, setBetaUpdating] = useState<Set<string>>(new Set());
  const handleToggleBeta = async (account: any) => {
    if (betaUpdating.has(account.id)) return;
    const nextValue = !account.is_beta;
    const previous = users;
    // Optimistic update with rollback on error.
    setUsers((current) => current.map((entry: any) => (entry.id === account.id ? { ...entry, is_beta: nextValue } : entry)));
    setBetaUpdating((current) => new Set(current).add(account.id));
    try {
      const response = await usersAPI.setBeta(account.id, nextValue);
      const updated = response.data?.data?.user ?? response.data?.user ?? response.data?.data;
      const updatedIsBeta = typeof updated === 'object' && updated !== null && typeof (updated as any).is_beta === 'boolean'
        ? (updated as any).is_beta
        : typeof response.data?.data?.is_beta === 'boolean' ? response.data.data.is_beta : undefined;
      if (typeof updatedIsBeta === 'boolean') {
        setUsers((current) => current.map((entry: any) => (entry.id === account.id ? { ...entry, is_beta: updatedIsBeta } : entry)));
      }
      setError('');
    } catch (err: any) {
      setUsers(previous);
      setError(err.response?.data?.error || err.response?.data?.message || 'Error al actualizar estado beta');
    } finally {
      setBetaUpdating((current) => { const next = new Set(current); next.delete(account.id); return next; });
    }
  };

  type SortKey = 'name' | 'email' | 'role' | 'condo' | 'unit';
  // FIX 2026-09-16 (live-QA bug, Jaime report): the `name === 'Cuenta eliminada'`
// heuristic hid RESURRECTED accounts. The soft-delete paths set that placeholder
// name, but a later confirm-email resurrection revives the row (deleted_at=NULL,
// anonymized=FALSE) while keeping the old name — and the panel then hid a live,
// active account. Deleted detection now relies ONLY on the authoritative
// columns (deleted_at / anonymized) and the legacy tombstone email pattern.
const isDeleted = (candidate: any) => Boolean(candidate.deleted_at || candidate.deletedAt || candidate.anonymized || String(candidate.email || '').endsWith('@removed.local'));
  const displayName = (candidate: any) => candidate.name || candidate.full_name || '-';
  type Membership = { role?: string; condoId?: string; condo_id?: string; condoName?: string; condo_name?: string; unit?: string; unitName?: string; unit_name?: string; unit_number?: string; unit_id?: string };
  type GroupedUser = { account: any; memberships: Membership[] };
  const membershipCondoName = (membership: Membership) => membership.condoId === 'all' ? 'Todos' : (membership.condoName || membership.condo_name || condos.find((c: any) => c.id === (membership.condoId || membership.condo_id))?.name || membership.condoId || membership.condo_id || '-');
  const membershipUnitName = (membership: Membership) => {
    const name = membership.unit || membership.unitName || membership.unit_name || membership.unit_number;
    if (name) return name;
    const id = membership.unit_id;
    if (!id) return '-';
    // Never show a full UUID: shorten to "Unit d17782…".
    return /^[0-9a-f]{8}-/i.test(id) ? `Unit ${id.slice(0, 6)}…` : id;
  };
  const groupedUsers = useMemo((): GroupedUser[] => {
    const grouped = new Map<string, GroupedUser>();
    users.forEach((candidate: any) => {
      const existing = grouped.get(candidate.id);
      const membership = { role: candidate.role, condoId: candidate.condoId, condo_id: candidate.condo_id, condoName: candidate.condoName, condo_name: candidate.condo_name, unit: candidate.unit, unitName: candidate.unitName, unit_name: candidate.unit_name, unit_number: candidate.unit_number, unit_id: candidate.unit_id };
      if (existing) existing.memberships.push(membership);
      else grouped.set(candidate.id, { account: candidate, memberships: [membership] });
    });
    return Array.from(grouped.values()).map((group) => ({ ...group, memberships: group.memberships.filter((membership, index, all) => all.findIndex((item) => JSON.stringify(item) === JSON.stringify(membership)) === index) }));
  }, [users, condos]);
  const deletedUserCount = useMemo(() => groupedUsers.filter((group) => isDeleted(group.account)).length, [groupedUsers]);
  const filteredUsers = useMemo(() => {
    const query = filter.trim().toLocaleLowerCase();
    return groupedUsers.filter((group) => !isDeleted(group.account)).filter((group) => {
      const searchable = [displayName(group.account), group.account.email, ...group.memberships.flatMap((membership) => [membershipCondoName(membership), getRoleLabel(displayRole(membership, group.account)), membershipUnitName(membership)])];
      return !query || searchable.some((value) => String(value || '').toLocaleLowerCase().includes(query));
    }).sort((a, b) => {
      const primaryA = a.memberships[0] || {}, primaryB = b.memberships[0] || {};
      const values: Record<SortKey, [string, string]> = {
        name: [displayName(a.account), displayName(b.account)], email: [a.account.email || '', b.account.email || ''],
        role: [getRoleLabel(displayRole(primaryA, a.account)), getRoleLabel(displayRole(primaryB, b.account))], condo: [membershipCondoName(primaryA), membershipCondoName(primaryB)], unit: [membershipUnitName(primaryA), membershipUnitName(primaryB)],
      };
      const comparison = values[sort.key][0].localeCompare(values[sort.key][1], 'es', { sensitivity: 'base' });
      return sort.direction === 'asc' ? comparison : -comparison;
    });
  }, [groupedUsers, filter, sort]);

  const sortHeader = (key: SortKey, label: string) => (
    <button type="button" onClick={() => setSort((current) => ({ key, direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc' }))} className="inline-flex items-center gap-1 text-left hover:text-[var(--v-accent)]">
      {label}<span aria-hidden="true" className="text-sm">{sort.key === key ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
    </button>
  );

  const getRoleBadgeColor = (role: string) => {
    const colors: Record<string, string> = {
      super_admin: 'bg-purple-100 text-purple-800',
      admin: 'bg-blue-100 text-blue-800',
      sub_admin: 'bg-indigo-100 text-indigo-800',
      directive: 'bg-green-100 text-green-800',
      treasurer: 'bg-green-100 text-green-800',
      propietario: 'bg-teal-100 text-teal-800',
      resident: 'bg-gray-100 dark:bg-[var(--v-surface-1)] text-gray-800 dark:text-slate-100',
      visitante: 'bg-slate-100 dark:bg-[var(--v-surface-2)] text-slate-600 dark:text-slate-300',
      familiar: 'bg-orange-100 text-orange-800',
      empleado: 'bg-amber-100 text-amber-800',
      security: 'bg-yellow-100 text-yellow-800',
      provider: 'bg-pink-100 text-pink-800',
      maintenance_staff: 'bg-stone-100 text-stone-800',
    };
    return colors[role] || 'bg-gray-100 dark:bg-[var(--v-surface-1)] text-gray-800 dark:text-slate-100';
  };

  // FIX 2026-09-16 (Jaime addendum to live-QA items 4/5): role DISPLAY for
  // unit-less accounts. users.role stays 'resident' on claim-created accounts
  // (changing the stored value would ripple into /login role resolution,
  // requireRole gates and entitlements), so the mapping is display-only:
  //   - an account with a businesses row (supplier) shows 'Proveedor';
  //   - a 'resident' account with NO condo membership and NO unit shows
  //     'Visitante' until it gains a unit/condo link;
  //   - everything else keeps its real role label.
  // Hoisted as a function declaration (FIX 2026-09-16 live crash): the
  // filteredUsers/sort memos above call displayRole during render; as a const
  // arrow defined AFTER those memos it was in the temporal dead zone and threw
  // "Cannot access 'displayRole' before initialization" the moment users
  // loaded, triggering the error boundary on /user-management.
  // BUG #157: rebuilds the exact membership object the row renderer feeds to
  // displayRole, so the modal prefill and the roleChanged guard share the row's
  // source of truth. Kept as a hoisted function declaration like displayRole.
  function effectiveMembershipRole(candidate: any): string {
    const membership: Membership = { role: candidate.role, condoId: candidate.condoId, condo_id: candidate.condo_id, condoName: candidate.condoName, condo_name: candidate.condo_name, unit: candidate.unit, unitName: candidate.unitName, unit_name: candidate.unit_name, unit_number: candidate.unit_number, unit_id: candidate.unit_id };
    return displayRole(membership, candidate);
  }

  function displayRole(membership: Membership, account: any): string {
    const role = membership.role || account?.user_role || account?.role || 'resident';
    if (role !== 'resident') return role;
    if (account?.is_supplier) return 'provider';
    const hasCondo = Boolean(membership.condoId || membership.condo_id);
    const hasUnit = Boolean(membership.unit || membership.unitName || membership.unit_name || membership.unit_number || membership.unit_id);
    if (!hasCondo && !hasUnit) return 'visitante';
    return role;
  }

  function getRoleLabel(role: string) {
    const labels: Record<string, string> = {
      super_admin: 'Super Admin',
      admin: 'Administrador',
      sub_admin: 'Sub Administrador',
      directive: 'Directivo',
      treasurer: 'Tesorero',
      propietario: 'Propietario',
      resident: 'Residente (Inquilino)',
      visitante: 'Visitante',
      familiar: 'Familiar',
      empleado: 'Empleado',
      security: 'Seguridad',
      provider: 'Proveedor',
      maintenance_staff: 'Mantenimiento',
    };
    return labels[role] || role;
  }

  if (user?.role !== 'admin' && user?.role !== 'super_admin' && user?.role !== 'sub_admin') {
    return (
      <div className="min-h-full flex items-center justify-center bg-[var(--v-base-bg)]">
        <div className="bg-white dark:bg-[var(--v-surface-1)] p-8 rounded-lg shadow-md text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Acceso Denegado</h2>
          <p className="text-gray-600 dark:text-slate-300">Solo administradores pueden gestionar usuarios.</p>
          <button
            onClick={() => navigate('/home/dashboard')}
            className="mt-4 px-4 py-2 bg-[#36CCD8] text-white rounded hover:bg-[#2DB8C3]"
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[var(--v-base-bg)] py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <button
              onClick={() => navigate('/home/dashboard')}
              className="text-[#36CCD8] hover:text-[#2DB8C3] flex items-center mb-2"
            >
              ← Volver al Dashboard
            </button>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-[var(--v-text-primary)]">Gestión de Usuarios</h1>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-[#36CCD8] text-white rounded hover:bg-[#2DB8C3] transition-colors"
          >
            + Agregar Usuario
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="mb-4 flex items-center gap-3 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-[var(--v-surface-1)] p-3">
          <label htmlFor="user-filter" className="sr-only">Filtrar usuarios</label>
          <input id="user-filter" type="search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Buscar por nombre, email o condominio…" className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-transparent px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-[#36CCD8] focus:outline-none" />
          {deletedUserCount > 0 && <p className="text-xs text-gray-500 dark:text-slate-400">{deletedUserCount} {deletedUserCount === 1 ? 'cuenta eliminada oculta' : 'cuentas eliminadas ocultas'}</p>}
        </div>

        {loading ? (
          <div className="text-center py-8">Cargando usuarios...</div>
        ) : (
          <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-lg shadow overflow-hidden">
            <table className="w-full table-fixed divide-y divide-gray-200">
              <colgroup>{columnKeys.map((key) => <col key={key} style={{ width: `${columnWidths[key]}%` }} />)}</colgroup>
              <thead className="bg-gray-50 dark:bg-slate-950">
                <tr>
                  {(['name', 'email', 'role', 'condo', 'unit'] as ColumnKey[]).map((key) => {
                    const labels: Record<string, string> = { name: 'Nombre', email: 'Email', role: 'Rol', condo: 'Condominio', unit: 'Unidad' };
                    return <th key={key} className="relative px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                      {sortHeader(key as SortKey, labels[key])}
                      <span role="separator" aria-label={`Cambiar ancho de ${labels[key]}`} onPointerDown={(event) => beginColumnResize(event, key)} className="absolute inset-y-0 right-0 z-10 w-1.5 cursor-col-resize touch-none hover:bg-[var(--v-accent)]" />
                    </th>;
                  })}
                  <th className="relative px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-[var(--v-surface-1)] divide-y divide-gray-200">
                {filteredUsers.map((group) => {
                  const deleted = isDeleted(group.account);
                  const showBetaToggle = isSuperAdminViewer && typeof group.account.is_beta === 'boolean';
                  return <tr key={group.account.id} className={deleted ? 'bg-gray-50/70 dark:bg-slate-900/60 opacity-60 italic' : ''}>
                    <td className="max-w-0 px-3 py-4 text-sm font-medium text-gray-900 dark:text-white"><span className="block truncate" title={displayName(group.account)}>{displayName(group.account)}</span>{deleted && <span className="ml-2 text-xs font-normal text-gray-500 dark:text-slate-400">Cuenta eliminada</span>}</td>
                    <td className="max-w-0 px-3 py-4 text-sm text-gray-500 dark:text-slate-400"><span className="block truncate" title={group.account.email || '-'}>{group.account.email || '-'}</span></td>
                    <td className="max-w-0 px-3 py-4 text-sm text-gray-500 dark:text-slate-400"><div className="flex min-w-0 flex-col items-start gap-1">{group.memberships.map((membership, index) => <span key={`${membership.role}-${membership.condo_id || membership.condoId}-${index}`} className={`max-w-full truncate px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(displayRole(membership, group.account))}`} title={getRoleLabel(displayRole(membership, group.account))}>{getRoleLabel(displayRole(membership, group.account))}</span>)}{showBetaToggle && (
                      <button type="button" onClick={() => handleToggleBeta(group.account)} disabled={betaUpdating.has(group.account.id)} aria-pressed={Boolean(group.account.is_beta)} title={group.account.is_beta ? 'Usuario beta — clic para quitar' : 'Marcar como usuario beta'} className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full border transition-colors disabled:opacity-50 ${group.account.is_beta ? 'bg-purple-600 border-purple-600 text-white hover:bg-purple-700' : 'bg-transparent border-purple-300 text-purple-700 hover:bg-purple-50 dark:text-purple-300 dark:border-purple-700'}`}>
                        {betaUpdating.has(group.account.id) ? '…' : 'Beta'}
                      </button>
                    )}</div></td>
                    <td className="max-w-0 px-3 py-4 text-sm text-gray-500 dark:text-slate-400"><div className="flex min-w-0 flex-col gap-1">{group.memberships.map((membership, index) => <span key={`${membership.role}-${membership.condo_id || membership.condoId}-${index}`} className="block max-w-full truncate" title={membershipCondoName(membership)}>{membershipCondoName(membership)}</span>)}</div></td>
                    <td className="max-w-0 px-3 py-4 text-sm text-gray-500 dark:text-slate-400"><div className="flex min-w-0 flex-col gap-1">{group.memberships.map((membership, index) => <span key={`${membership.role}-${membership.condo_id || membership.condoId}-${index}`} className="block max-w-full truncate" title={membershipUnitName(membership)}>{membershipUnitName(membership)}</span>)}</div></td>
                    <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">{!deleted && <><button onClick={() => openEditUser(group.account)} className="mr-3 text-[#36CCD8] hover:text-[#2DB8C3]">Editar</button><button onClick={() => handleDeleteUser(group.account.id)} className="text-red-600 hover:text-red-900" disabled={group.account.role === 'super_admin'}>Eliminar</button></>}</td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
        )}

        {editingUser && (
          <div className="fixed inset-0 z-20 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="edit-user-title">
            <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-lg p-8 max-w-md w-full">
              <h2 id="edit-user-title" className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Editar Usuario</h2>
              <form onSubmit={handleEditUser} className="space-y-4">
                {([['name', 'Nombre', 'text'], ['email', 'Email', 'email'], ['phone', 'Teléfono', 'tel']] as const).map(([key, label, type]) => (
                  <div key={key}><label className="block text-sm font-medium text-gray-700 dark:text-slate-200">{label}</label><input type={type} required={key !== 'phone'} value={editForm[key]} onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-[var(--v-text-primary)] dark:border-slate-600 dark:bg-[var(--v-surface-2)]" /></div>
                ))}
                {editingUser.role && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-200">Rol (membresía)</label>
                    <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-[var(--v-text-primary)] dark:border-slate-600 dark:bg-[var(--v-surface-2)]">
                      <option value="" disabled>— mantener rol actual —</option>
                      {EDITABLE_ROLES.map((r) => (
                        <option key={r} className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" value={r}>{getRoleLabel(r)}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="flex space-x-4 mt-6"><button type="button" onClick={() => setEditingUser(null)} className="flex-1 py-2 px-4 border border-gray-300 dark:border-slate-600 rounded-md">Cancelar</button><button type="submit" disabled={savingEdit} className="flex-1 py-2 px-4 bg-[#36CCD8] text-white rounded-md hover:bg-[#2DB8C3] disabled:opacity-50">{savingEdit ? 'Guardando…' : 'Guardar'}</button></div>
              </form>
            </div>
          </div>
        )}

        {showAddModal && (
          <div className="fixed inset-0 z-20 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="add-user-title">
            <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-lg p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h2 id="add-user-title" className="text-2xl font-bold mb-4">Agregar Nuevo Usuario</h2>
              {error && <div className="mb-4 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 px-3 py-2 text-sm text-red-600 dark:text-red-300">{error}</div>}
              <form onSubmit={handleAddUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200">Nombre</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-[var(--v-text-primary)] dark:border-slate-600 dark:bg-[var(--v-surface-2)]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-[var(--v-text-primary)] dark:border-slate-600 dark:bg-[var(--v-surface-2)]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200">Contraseña</label>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-[var(--v-text-primary)] dark:border-slate-600 dark:bg-[var(--v-surface-2)]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200">Rol</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-[var(--v-text-primary)] dark:border-slate-600 dark:bg-[var(--v-surface-2)]"
                  >
                    <option className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" value="super_admin">Super Admin</option>
                    <option className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" value="admin">Administrador</option>
                    <option className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" value="sub_admin">Sub Administrador</option>
                    <option className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" value="directive">Directivo</option>
                    <option className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" value="treasurer">Tesorero</option>
                    <option className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" value="propietario">Propietario</option>
                    <option className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" value="resident">Residente (Inquilino)</option>
                    <option className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" value="familiar">Familiar</option>
                    <option className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" value="empleado">Empleado</option>
                    <option className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" value="security">Seguridad</option>
                    <option className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" value="provider">Proveedor</option>
                    <option className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" value="maintenance_staff">Mantenimiento</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200">Condominio</label>
                  <select
                    value={formData.condoId}
                    onChange={(e) => setFormData({ ...formData, condoId: e.target.value })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-[var(--v-text-primary)] dark:border-slate-600 dark:bg-[var(--v-surface-2)]"
                  >
                    <option className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" value="">Seleccionar...</option>
                    {condos.map((c: any) => (
                      <option className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                {(formData.role === 'resident' || formData.role === 'propietario' || formData.role === 'familiar') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-200">Unidad</label>
                    <select value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })} disabled={!formData.condoId} required className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-[var(--v-text-primary)] dark:border-slate-600 dark:bg-[var(--v-surface-2)]">
                      <option className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" value="">Seleccionar unidad...</option>
                      {units.map((unit: any) => <option className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" key={unit.id} value={unit.id}>{formatUnitLabel(unit, unit.unitNumber || unit.unit_number || 'Unidad')}</option>)}
                    </select>
                  </div>
                )}
                <div className="flex space-x-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 py-2 px-4 border border-gray-300 dark:border-slate-600 rounded-md"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-2 px-4 bg-[#36CCD8] text-white rounded-md hover:bg-[#2DB8C3]"
                  >
                    Guardar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
